<template>
  <div class="container-fluid h-100 p-0 pt-3 pb-5">
    <div class="card customer-card shadow-sm h-100">
      <div class="block-padding">
        <div class="d-flex justify-content-between">
          <div>
            <h3 class="mb-2 customer-h3-title">
              Create From Template
            </h3>
            <p class="text-muted mb-0">
              Select a template to start with
            </p>
          </div>
          <button
            class="btn btn-new-creative d-flex align-items-center text-nowrap gap-2"
            :disabled="!selectedTemplateIds.length"
            data-ai-agent-id="new-creative-btn"
            data-ai-agent-type="button"
            data-ai-agent-label="New Creative"
            @click="createNewCreative"
          >
            <img
              src="/images/customer/plus.png"
              width="20"
              height="20"
            >
            New Creative
          </button>
        </div>
        <div class="d-flex justify-content-end">
          <CustomersDropdown
            ref="customersDropdown"
            :customers="customers"
            @select="onSelectCustomer"
          />
        </div>
        <div class="d-flex justify-content-between align-items-center mb-3 b-header">
          <h5 class="mb-0 customer-h5">
            Template Categories
          </h5>
          <ul class="category-filter p-3 m-0 d-flex">
            <li
              v-for="category in categories"
              :key="category"
              class="category-item"
              :class="{ active: activeCategory === category }"
              @click="filterCategory(category)"
            >
              {{ category }}
            </li>
          </ul>
          <div class="search-wrapper">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control search-input"
              placeholder="Search"
              data-ai-agent-type="input"
              data-ai-agent-id="templates-search-input"
              data-ai-agent-label="Templates Search"
              @keyup.enter="loadTemplates"
              @input="loadTemplates"
            >
          </div>
        </div>
        <div 
          class="templates-block d-flex flex-wrap"
          data-ai-agent-type="template-list"
          data-ai-agent-id="templates-list"
          data-ai-agent-label="Templates List"
        >
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="template-card-col"
            :data-ai-template-id="template.id"
            :data-ai-template-name="template.name"
          >
            <div
              class="card template-card"
              :class="{ selected: isSelected(template.id) }"
              @click="toggleSelection(template.id)"
            >
              <div class="h-100 d-flex justify-content-center align-items-center">
                <img
                  :src="template.image || '/images/customer/default-template-image.png'"
                  height="102"
                  width="102"
                  alt="Template Image"
                >
              </div>
              <div class="template-card-body d-flex align-items-center justify-content-between">
                <h5 class="template-card-title">
                  {{ truncatedName(template.name) }}
                </h5>
                <button
                  class="btn btn-select"
                  @click.stop.prevent="toggleSelection(template.id)"
                >
                  {{ isSelected(template.id) ? 'Deselect' : 'Select' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        data-ai-agent-type="pagination"
        data-ai-agent-id="templates-pagination"
        data-ai-agent-label="Templates Pagination"
      >
        <VuePagination
          v-if="filteredTemplates.length > 0"
          :pagination.sync="pagination"
          class-name="hero-files-pagination"
          @paginate="loadTemplates"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CustomersDropdown from "@frontend/components/common/dashboard/banner-builder/components/CustomersDropdown.vue";
import VuePagination from "@frontend/components/common/PaginationComponent.vue";

const DEFAULT_CATEGORY = "Default";

export default {
  name: "TemplatesDashboard",
  components: { 
    CustomersDropdown,
    VuePagination
  },
  data() {
    return {
      categories: [
        DEFAULT_CATEGORY,
        "Black Friday",
        "Summer",
        "Health Awareness",
        "Halloween",
        "Cyber Monday",
        "Text",
        "Christmas",
      ],
      activeCategory: null,
      searchQuery: "",
      selectedCustomerId: 0,
      pagination: {
        data: [],
        total: 0,
        current_page: 1,
        last_page: 1,
      },
    };
  },
  computed: {
    ...mapGetters('customers', ['customers']),
    ...mapGetters('templates', [
      'templates',
      'loading',
      'totalPages',
      'selectedTemplateIds',
      'isSelected',
      'response',
    ]),
    filteredTemplates() {
      return this.templates.filter(
        (t) =>
          (!this.activeCategory || t.category === this.activeCategory || this.activeCategory === DEFAULT_CATEGORY )
      );
    },
  },
  mounted() {
    this.loadCustomers();
    this.activeCategory = DEFAULT_CATEGORY;
    this.loadTemplates();
  },
  methods: {
    ...mapActions('customers', ['fetchCustomers']),
    ...mapActions('templates', ['fetchTemplates', 'getSelectedDataWithoutGrid']),
    ...mapMutations('templates', ['TOGGLE_SELECTED_TEMPLATE', 'CLEAR_SELECTION']),
    ...mapActions('placements', ['createPlacement']),
    loadCustomers() {
      this.fetchCustomers()
    },
    onSelectCustomer(customer) {
      this.selectedCustomerId = customer.id
      this.pagination.current_page = 1;
      this.loadTemplates()
    },
    async loadTemplates() {
      await this.fetchTemplates({
        customerId: this.selectedCustomerId,
        search: this.searchQuery,
        page: this.pagination.current_page,
        limit: 10,
      })

      this.pagination = this.response;
    },
    async createNewCreative() { 
      const settings = await this.getSelectedDataWithoutGrid();
      const response = await this.createPlacement({
        name: 'New Placement',
        folder_id: '',
        region_id: 1,
        'grid-view-template': 1,
        settings: JSON.stringify(settings) 
      });
      
      const layoutId = response?.id;
      window.location.href = `/banner-builder/manage-assets?layoutId=${layoutId}`;
    },
    filterCategory(category) {
      this.activeCategory = category
    },
    toggleSelection(templateId) {
      this.TOGGLE_SELECTED_TEMPLATE(templateId)
    },
    truncatedName(name) {
      const maxLength = 25;

      if (name.length <= maxLength) return name;

      const start = name.slice(0, Math.ceil(maxLength / 2) - 3);
      const end = name.slice(-Math.floor(maxLength / 2) + 3);
      return `${start}...${end}`;
    },
  },
};
</script>

<style scoped lang="scss">
.customer-h3-title {
  font-weight: 500;
  font-size: 30px;
  line-height: 38px;
  color: #101828;
}

.block-padding {
  padding: 32px;
}

.customer-h5 {
  color: #101828;
  font-weight: 400;
  font-size: 24px;
  line-height: 32px;
}

.search-input {
  background: url('/images/customer/search.png') no-repeat 12px center;
  background-size: 20px;
  padding-left: 38px;
  border: 1px solid #D0D5DD;
  border-radius: 8px;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #667085;
}

.search-input:focus {
  border: 1px solid #D0D5DD !important;
  color: #667085 !important;
  box-shadow: 0px 1px 2px 0px #1018280D !important;
}

.templates-block {
  padding: 8px;
  border: 1px solid #EAECF0 !important;
  border-radius: 8px !important;
}

.template-card {
  height: 100%;
  background: #D9D9D9 !important;
  border: none !important;
  border-bottom-left-radius: 10px !important;
  border-bottom-right-radius: 10px !important;
}

.template-card-col {
  flex: 0 0 20%; /* 100% / 5 = 20% */
  max-width: 20%;
  padding: 10px 10px 0px 10px;
  height: 270px;
  max-height: 270px;
}

.template-card-body {
  padding: 12px !important;
  background: #F6F7F8;
  height: 64px;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
}

.template-card-title {
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  margin-right: 5px;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
}

.btn-select {
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  background: #041A4F;
  border: 1px solid #7F56D9;
  box-shadow: 0px 1px 2px 0px #1018280D;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
}

.btn-select:hover,
.btn-select:focus {
  background: #041A4F !important;
  color: #FFFFFF !important;
  border: 1px solid #7F56D9 !important;
  box-shadow: 0px 1px 2px 0px #1018280D;
}

.btn-group .btn {
  border-radius: 0; /* remove rounding between buttons */
}

.btn-group .btn:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.btn-group .btn:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.btn-new-creative {
  height: 40px;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #377E22;
  background: #377E22;
  box-shadow: 0px 1px 2px 0px #1018280D;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #FFFFFF;
}

.btn-new-creative:hover,
.btn-new-creative:focus {
  background: #377E22;
  border: 1px solid #377E22;
  color: #FFFFFF;
}

.filter-category-button {
  width: auto;
  height: 40px;
  padding: 10px 16px;
  border: 1px solid #D0D5DD;
  background: #FFFFFF;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054 !important;
  margin: 0;
  border-radius: 0;
}

.filter-category-button:hover,
.filter-category-button:focus {
  background: #D0D5DD59 !important;
  color: #344054 !important;
  box-shadow: 0px 1px 2px 0px #1018280D !important;
}

.filter-category-button:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.filter-category-button:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.filter-category-button + .filter-category-button {
  border-left: none;
}

.pagination-block {
  border-top: 1px solid #EAECF0;
  padding: 12px 24px 16px 34px;
}

.pagination-block ul {
  height: 40px !important;
}

.page-item:first-child .page-link {
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

.page-item:last-child .page-link {
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
}

.page-link {
  height: 100% !important;
  display: flex;
  align-items: center !important;
}

.pagination-text,
.pagination-block ul li a {
  font-weight: 500 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  color: #1D2939 !important;
}

.customer-card {
  border-top-left-radius: 40px !important;
  border-bottom-left-radius: 40px !important;
}

.c-main > .container-fluid{
  padding-right: 0px !important;
  height: 100%;
}
.template-card.selected {
  border: 2px solid #377E22 !important;
  background: #EAF6EA !important;
  transform: scale(1.02);
  transition: all 0.2s ease;
}

.btn-new-creative:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.b-header {
  flex-wrap: wrap;
}
.b-buttons {
  flex-wrap: wrap;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

@media (max-width: 1325px) {
  .search-wrapper {
    order: 2;
    width: 100%;
    margin-left: 0;
  }

  .b-buttons {
    order: 1;
    width: 100%;
    justify-content: flex-start;
    padding-left: 0 !important;
  }
}
.category-filter {
  display: inline-flex;
  border-radius: 8px;
  overflow: hidden;
}
.category-item {
  list-style: none;
  padding: 8px 14px;
  cursor: pointer;
  background: #FFFFFF;
  border: 1px solid #D0D5DD;
  color: #344054 !important;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}

.category-item.active {
  background: #D0D5DD59;
}
.category-item + .category-item {
  border-left: none;
}

.category-filter > .category-item:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.category-filter > .category-item:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}
</style>
